import userEndpoints from '@/lib/api/endpoints/userEndpoints';
import authEndpoints from '@/lib/api/endpoints/authEndpoints';
import type { ApplicationUser, ProfileModel } from '@/lib/models/applicationUser';
import type { ODataResponse } from '@/lib/models/common';
import { zodValidator } from '@/lib/utils/zodValidator';
import { ApplicationUserSchema, ProfileModelSchema } from '@/lib/models/applicationUser';
import { useAuthStore } from '@/stores/auth';

/**
 * Interfaccia per il service degli User
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 * Wrapper che estrae automaticamente i dati dalle response Fetch
 */
export interface UsersService {
	/**
	 * Recupera lista paginata di users con query OData
	 * @param query - Query string OData
	 * @returns Promise con ODataResponse di ApplicationUser
	 */
	getUsers(query: string): Promise<ODataResponse<ApplicationUser>>;

	/**
	 * Recupera un singolo user per ID
	 * @param id - ID dell'utente
	 * @returns Promise con ApplicationUser
	 */
	getUser(id: string): Promise<ApplicationUser>;

	/**
	 * Aggiorna un user esistente
	 * @param id - ID dell'utente da aggiornare
	 * @param payload - Dati da aggiornare
	 * @returns Promise con ApplicationUser aggiornato
	 */
	updateUser(id: string, payload: ApplicationUser): Promise<ApplicationUser>;

	/**
	 * Crea un nuovo user
	 * @param payload - Dati del nuovo utente
	 * @returns Promise con ApplicationUser creato
	 */
	postUser(payload: ApplicationUser): Promise<ApplicationUser>;

	/**
	 * Elimina un user
	 * @param id - ID dell'utente da eliminare
	 * @returns Promise void
	 */
	deleteUser(id: string): Promise<void>;

	/**
	 * Aggiunge un ruolo all'utente
	 * @param id - ID dell'utente
	 * @param roleId - ID del ruolo da aggiungere
	 * @returns Promise void
	 */
	addRoleToUser(id: string, roleId: string): Promise<void>;

	/**
	 * Rimuove un ruolo dall'utente
	 * @param id - ID dell'utente
	 * @param roleId - ID del ruolo da rimuovere
	 * @returns Promise void
	 */
	removeRoleFromUser(id: string, roleId: string): Promise<void>;

	/**
	 * Cambia lo stato attivo/inattivo dell'utente
	 * @param id - ID dell'utente
	 * @returns Promise void
	 */
	toggleEnablingStatus(id: string): Promise<void>;

	/**
	 * Recupera il ruolo per ID utente
	 * @param id - ID dell'utente
	 * @returns Promise con string (primo ruolo dell'utente)
	 */
	getRoleByUserId(id: string): Promise<string>;

	/**
	 * Reset password utente
	 * @param id - ID dell'utente
	 * @param newPassword - Nuova password
	 * @returns Promise void
	 */
	resetPassword(id: string, newPassword: string): Promise<void>;

	/**
	 * Recupera il profilo dell'utente corrente
	 * @returns Promise con ProfileModel
	 */
	getProfile(): Promise<ProfileModel>;

	/**
	 * Aggiorna il profilo dell'utente corrente
	 * @param payload - Dati del profilo da aggiornare (ProfileModel)
	 * @returns Promise con ProfileModel
	 */
	updateProfile(payload: ProfileModel): Promise<ProfileModel>;

	/**
	 * Impersona un utente
	 * @param id - ID dell'utente da impersonare
	 * @returns Promise con boolean (successo dell'impersonificazione)
	 */
	impersonateUser(id: string): Promise<boolean>;

	/**
	 * Recupera informazioni utente corrente (helper per impersonateUser)
	 * @returns Promise void
	 */
	getUserInfo(): Promise<void>;

	/**
	 * Genera Excel per users
	 * @param query - Query string per il filtro
	 * @returns Promise con Blob Excel
	 */
	generateExcel(query: string): Promise<Blob>;
}

/**
 * Implementazione del service per gli User
 * Mantiene esattamente gli stessi nomi dei metodi del JavaScript originale
 * Estrae automaticamente la proprietà .data dalle response Fetch
 * Include validazione runtime centralizzata con zodValidator
 */
const usersService: UsersService = {
	async getUsers(query: string): Promise<ODataResponse<ApplicationUser>> {
		const response = (await userEndpoints.getUsers(query)).data;
		// Validazione OData response con schema ApplicationUser
		zodValidator.validateODataResponse(response, ApplicationUserSchema, 'ApplicationUser.getAll');
		return response;
	},

	async getUser(id: string): Promise<ApplicationUser> {
		const response = (await userEndpoints.getUser(id)).data;
		zodValidator.validateSingle(response, ApplicationUserSchema, 'ApplicationUser.getById');
		return response;
	},

	async updateUser(id: string, payload: ApplicationUser): Promise<ApplicationUser> {
		const response = (await userEndpoints.updateUser(id, payload)).data;
		zodValidator.validateSingle(response, ApplicationUserSchema, 'ApplicationUser.update');
		return response;
	},

	async postUser(payload: ApplicationUser): Promise<ApplicationUser> {
		const response = (await userEndpoints.postUser(payload)).data;
		zodValidator.validateSingle(response, ApplicationUserSchema, 'ApplicationUser.create');
		return response;
	},

	async deleteUser(id: string): Promise<void> {
		return (await userEndpoints.deleteUser(id)).data;
	},

	async addRoleToUser(id: string, roleId: string): Promise<void> {
		return (await userEndpoints.addRoleToUser(id, roleId)).data;
	},

	async removeRoleFromUser(id: string, roleId: string): Promise<void> {
		return (await userEndpoints.removeRoleFromUser(id, roleId)).data;
	},

	async toggleEnablingStatus(id: string): Promise<void> {
		return (await userEndpoints.toggleEnablingStatus(id)).data;
	},

	async getRoleByUserId(id: string): Promise<string> {
		return (await userEndpoints.getRoleByUserId(id)).data;
	},

	async resetPassword(id: string, newPassword: string): Promise<void> {
		return (await userEndpoints.resetPassword(id, newPassword)).data;
	},

	async getProfile(): Promise<ProfileModel> {
		const response = (await userEndpoints.getProfile()).data;
		zodValidator.validateSingle(response, ProfileModelSchema, 'ProfileModel.getProfile');
		return response;
	},

	async updateProfile(payload: ProfileModel): Promise<ProfileModel> {
		const response = (await userEndpoints.updateProfile(payload)).data;
		zodValidator.validateSingle(response, ProfileModelSchema, 'ProfileModel.updateProfile');
		return response;
	},

	async impersonateUser(id: string): Promise<boolean> {
		const authStore = useAuthStore();
		const token = (await userEndpoints.impersonateUser(id)).data;
		if (token) {
			authStore.setAccessToken(token);
			await this.getUserInfo();
			return true;
		}
		return false;
	},

	async getUserInfo(): Promise<void> {
		try {
			const userInfo = (await authEndpoints.getUserInfo()).data;
			if (userInfo) {
				const authStore = useAuthStore();
				authStore.setUserInfo(userInfo);
			}
		} catch {
			// Note: nel JS originale chiama this.logout() ma non è definito
			// Lasciamo vuoto per ora per evitare errori
			console.error('❌ Failed to get user info after impersonation');
		}
	},

	async generateExcel(query: string): Promise<Blob> {
		return (await userEndpoints.generateExcel(query)).data;
	},
};

export default usersService;
